/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

// oxlint-disable sort-keys
import { Code, TextLink, Typography } from '@neo4j-ndl/react';
import classNames from 'classnames';
import { memo, useMemo, useRef } from 'react';
import { Streamdown } from 'streamdown';

import { type CommonProps } from '../../_common/types';
import { type LanguageProp, Languages } from '../../code-block/code-languages';
import { CodePreview } from '../code-preview';

type ResponseProps = {
  /** The response from the AI */
  children: string;
  /** Whether the response is animating */
  isAnimating?: React.ComponentProps<typeof Streamdown>['isAnimating'];
};

/**
 * The component is used to display the response from an LLM.
 * It handles markdown rendering and streaming.
 *
 * @alpha - Changes to this component may be breaking.
 */
const Response = memo(
  ({
    className,
    children,
    isAnimating = false,
    ref,
    style,
    htmlAttributes,
    ...restProps
  }: CommonProps<'div', ResponseProps>) => {
    // Use a ref to store the current markdown content so we can check for unclosed fences
    // without recreating the components object on every render
    const childrenRef = useRef(children);
    childrenRef.current = children;

    const components: React.ComponentProps<typeof Streamdown>['components'] =
      useMemo(
        () => ({
          // oxlint-disable-next-line no-unused-vars
          ol: ({ node, children, className, ref: _ref, ...props }) => (
            <ol className={className} {...props}>
              {children}
            </ol>
          ),
          // oxlint-disable-next-line no-unused-vars
          ul: ({ node, children, className, ref: _ref, ...props }) => (
            <ul className={classNames(className)} {...props}>
              {children}
            </ul>
          ),
          // oxlint-disable-next-line no-unused-vars
          li: ({ node, children, className, ref: _ref, ...props }) => (
            <li className={className} {...props}>
              {children}
            </li>
          ),
          // oxlint-disable-next-line no-unused-vars
          hr: ({ node, className, ref: _ref, ...props }) => (
            <hr className={classNames(className)} {...props} />
          ),
          // oxlint-disable-next-line no-unused-vars
          strong: ({
            node: _node,
            children,
            className,
            style,
            ref: _ref,
            ...props
          }) => (
            <Typography
              as="strong"
              variant="body-medium"
              className={className}
              style={{ ...style, fontWeight: 'bold' }}
              {...props}
            >
              {children}
            </Typography>
          ),
          // oxlint-disable-next-line no-unused-vars
          em: ({ node, children, className, style, ref: _ref, ...props }) => (
            <Typography
              as="em"
              variant="body-medium"
              className={className}
              style={{ ...style, fontStyle: 'italic' }}
              {...props}
            >
              {children}
            </Typography>
          ),
          // oxlint-disable-next-line no-unused-vars
          a: ({ node, children, className, href, ...props }) => (
            <TextLink
              type="internal-underline"
              className={className}
              href={href}
              htmlAttributes={props}
            >
              {children}
            </TextLink>
          ),
          // oxlint-disable-next-line no-unused-vars
          h1: ({ node, children, className, ...props }) => (
            <h1 className={className} {...props}>
              {children}
            </h1>
          ),
          // oxlint-disable-next-line no-unused-vars
          h2: ({ node, children, className, ...props }) => (
            <h2 className={className} {...props}>
              {children}
            </h2>
          ),
          // oxlint-disable-next-line no-unused-vars
          h3: ({ node, children, className, ...props }) => (
            <h3 className={className} {...props}>
              {children}
            </h3>
          ),
          // oxlint-disable-next-line no-unused-vars
          h4: ({ node, children, className, ...props }) => (
            <h4 className={className} {...props}>
              {children}
            </h4>
          ),
          // oxlint-disable-next-line no-unused-vars
          h5: ({ node, children, className, ...props }) => (
            <h5 className={className} {...props}>
              {children}
            </h5>
          ),
          // oxlint-disable-next-line no-unused-vars
          h6: ({ node, children, className, ...props }) => (
            <h6 className={className} {...props}>
              {children}
            </h6>
          ),
          // oxlint-disable-next-line no-unused-vars
          table: ({ node, children, className, ref: _ref, ...props }) => (
            <div className="ndl-ai-response-table-wrapper">
              <table className={className} {...props}>
                {children}
              </table>
            </div>
          ),
          // oxlint-disable-next-line no-unused-vars
          thead: ({ node, children, className, ref: _ref, ...props }) => (
            <thead className={className} {...props}>
              {children}
            </thead>
          ),
          // oxlint-disable-next-line no-unused-vars
          tbody: ({ node, children, className, ref: _ref, ...props }) => (
            <tbody className={className} {...props}>
              {children}
            </tbody>
          ),
          // oxlint-disable-next-line no-unused-vars
          tr: ({ node, children, className, ref: _ref, ...props }) => (
            <tr className={className} {...props}>
              {children}
            </tr>
          ),
          // oxlint-disable-next-line no-unused-vars
          th: ({ node, children, className, ref: _ref, ...props }) => (
            <th className={className} {...props}>
              {children}
            </th>
          ),
          // oxlint-disable-next-line no-unused-vars
          td: ({ node, children, className, ref: _ref, ...props }) => (
            <td className={className} {...props}>
              {children}
            </td>
          ),
          // oxlint-disable-next-line no-unused-vars
          blockquote: ({ node, children, className, ref: _ref, ...props }) => (
            <blockquote className={classNames(className)} {...props}>
              {children}
            </blockquote>
          ),
          code: ({ node, className, children: codeChildren, ...props }) => {
            const isInline =
              node?.position?.start.line === node?.position?.end.line;

            const nodeClassNamesRaw = node?.properties?.className;
            const nodeClassNamesArray: string[] = [];

            if (typeof nodeClassNamesRaw === 'string') {
              nodeClassNamesArray.push.apply(
                nodeClassNamesArray,
                nodeClassNamesRaw.split(' '),
              );
            } else if (Array.isArray(nodeClassNamesRaw)) {
              nodeClassNamesArray.push.apply(
                nodeClassNamesArray,
                nodeClassNamesRaw as string[],
              );
            }

            let language: LanguageProp | 'text' = 'text';
            const rawLanguage = nodeClassNamesArray.find((string: string) =>
              string.startsWith('language-'),
            );
            if (rawLanguage !== undefined) {
              language =
                (rawLanguage.replace('language-', '') as LanguageProp) ??
                'text';
            }
            language = (Languages as readonly string[]).includes(language)
              ? (language as LanguageProp)
              : 'text';
            if (isInline) {
              return (
                <Code
                  isRunnable={false}
                  className={className}
                  htmlAttributes={props}
                >
                  {codeChildren}
                </Code>
              );
            }
            if (typeof codeChildren === 'string') {
              // Check if code fences are balanced in the markdown
              // If there are an odd number of fences, it means there's an unclosed code block
              const fenceMatches = childrenRef.current.match(/```/g);
              const fenceCount = fenceMatches ? fenceMatches.length : 0;
              const hasUnclosedFence = fenceCount % 2 !== 0;

              // Show loading state when there's an unclosed fence (code block is streaming)
              const isCodeBlockLoading = hasUnclosedFence;
              return (
                <CodePreview
                  key={props.key}
                  code={codeChildren}
                  isLoading={isCodeBlockLoading}
                  language={language}
                  className={className}
                  htmlAttributes={{
                    ...props,
                  }}
                />
              );
            } else {
              return <code className={className}>{codeChildren}</code>;
            }
          },
          // oxlint-disable-next-line no-unused-vars
          pre: ({ node, className, children, ref: _ref, ...props }) => {
            return (
              <pre className={className} {...props}>
                {children}
              </pre>
            );
          },
          // oxlint-disable-next-line no-unused-vars
          p: ({ node, children, className, ref: _ref, ...props }) => (
            <Typography
              as="p"
              variant="body-medium"
              className={classNames(className)}
              {...props}
            >
              {children}
            </Typography>
          ),
          // oxlint-disable-next-line no-unused-vars
          img: ({ node, className, src, alt, ref: _ref, ...props }) => (
            <img
              className={classNames(className)}
              src={src}
              alt={alt}
              {...props}
            />
          ),
        }),
        [],
      );

    return (
      <div
        ref={ref}
        style={style}
        className={classNames('ndl-ai-response', className)}
        {...restProps}
        {...htmlAttributes}
      >
        <Streamdown
          isAnimating={isAnimating}
          parseIncompleteMarkdown={isAnimating}
          components={components}
        >
          {children}
        </Streamdown>
      </div>
    );
  },
  (prevProps, nextProps) => prevProps.children === nextProps.children,
);

Response.displayName = 'Response';

export { Response };
